import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  onboardingApiClient,
  wxApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  WorkOrderTypeAPI,
  CreateWorkOrderTypeRequest,
  UpdateWorkOrderTypeRequest,
  WorkOrderSubTypeAPI,
  SlaRuleAPI,
  CreateSlaRuleRequest,
  UpdateSlaRuleRequest,
  ToggleSlaRuleRequest,
  TaxRateAPI,
  CreateTaxRateRequest,
  UpdateTaxRateRequest,
  ToggleTaxRateRequest,
  CertificationAPI,
  CreateCertificationRequest,
  UpdateCertificationRequest,
  ToggleCertificationRequest,
  SkillAPI,
  CreateSkillRequest,
  UpdateSkillRequest,
  ToggleSkillRequest,
  RoleAPI,
  CreateRoleRequest,
  UpdateRoleRequest,
  ToggleRoleRequest,
  RateAPI,
  CreateRateRequest,
  UpdateRateRequest,
  ToggleRateRequest,
  CreateReadingRequest,
  CreateUpdateApiResponse,
  ReadingsApiResponse,
  UpdateReadingRequest,
  CreateMaterialRequest,
  MaterialAPI,
  UpdateMaterialRequest,
  CreateTaskRequest,
  Skill,
  TaskAPI,
  UpdateTaskRequest,
  OperationTypesResponse,
  PPEEquipmentUpdateResponse,
  TogglePPEEquipmentRequest,
  UpdatePPEEquipmentRequest,
  PPEEquipmentCreateResponse,
  CreatePPEEquipmentRequest,
  PPEEquipmentResponse,
  CreateSafetyPackageRequest,
  SafetyPackageCreateResponse,
  SafetyPackageResponse,
  SafetyPackageUpdateResponse,
  ToggleSafetyPackageRequest,
  UpdateSafetyPackageRequest,
  CreateSafetyProtocolRequest,
  SafetyProtocolCreateResponse,
  SafetyProtocolResponse,
  SafetyProtocolUpdateResponse,
  ToggleSafetyProtocolRequest,
  UpdateSafetyProtocolRequest,
  ToggleMaterialRequest,
  ToggleReadingRequest,
  ToggleTaskRequest,
} from "./types";

export const workOrderTypesApi = {
  // Get all work order types
  getWorkOrderTypes: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: WorkOrderTypeAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      searchQuery ? "search/onboarding_config" : "config-map/sop_json",
      (query) => {
        if (!searchQuery) {
          query.push("remote_utility_id", utilityId.toString());
          query.push("config_level", "sop_type");
          query.push("show_inactive", "true");
        }

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("remote_utility_id", utilityId.toString());
          query.push("search_field", "sop_json-sop_type");
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  // Create new work order type
  createWorkOrderType: async (
    data: CreateWorkOrderTypeRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/sop_json");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  // Update work order type
  updateWorkOrderType: async (
    data: UpdateWorkOrderTypeRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/sop_json");
    const response = await onboardingApiClient.patch(url, data);
    return response.data;
  },

  // Toggle isActive status
  toggleWorkOrderTypeStatus: async (
    code: string,
    isActive: boolean,
    utilityId: string
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/sop_json");
    const response = await onboardingApiClient.patch(url, {
      code,
      is_active: isActive,
      remote_utility_id: utilityId,
    });
    return response.data;
  },
  getOperationTypes: async (): Promise<OperationTypesResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "choices/operation_type",
      (query) => {
        query.push("service_association", "all");
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },
};

export const workOrderSubTypesApi = {
  // Get all work order sub-types
  getWorkOrderSubTypes: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: WorkOrderSubTypeAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      searchQuery ? "search/onboarding_config" : "config-map/sop_json",
      (query) => {
        if (!searchQuery) {
          query.push("remote_utility_id", utilityId.toString());
          query.push("config_level", "sop_subtype");
          query.push("show_inactive", "true");
        }

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("remote_utility_id", utilityId.toString());
          query.push("search_field", "sop_json-sop_subtype");
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  // Create new work order sub-type
  createWorkOrderSubType: async (
    data: CreateWorkOrderTypeRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/sop_json");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  // Update work order sub-type
  updateWorkOrderSubType: async (
    data: UpdateWorkOrderTypeRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/sop_json");
    const response = await onboardingApiClient.patch(url, data);
    return response.data;
  },

  // Toggle isActive status
  toggleWorkOrderSubTypeStatus: async (
    code: string,
    isActive: boolean,
    utilityId: string
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/sop_json");
    const response = await onboardingApiClient.patch(url, {
      code,
      is_active: isActive,
      remote_utility_id: utilityId,
    });
    return response.data;
  },
};

export const slaRulesApi = {
  // Get all SLA rules
  getSlaRules: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: SlaRuleAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "rule",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new SLA rule
  createSlaRule: async (
    data: CreateSlaRuleRequest
  ): Promise<{ result: SlaRuleAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "rule");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update SLA rule
  updateSlaRule: async (
    ruleId: number,
    data: UpdateSlaRuleRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `rule/${ruleId}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle SLA rule status
  toggleSlaRuleStatus: async (
    ruleId: number,
    data: ToggleSlaRuleRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `rule-action/${ruleId}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};

export const taxRatesApi = {
  // Get all tax rates
  getTaxRates: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: TaxRateAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/tax_rate",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("show_inactive", "true");

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  taxRatesSearch: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: TaxRateAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "search/onboarding_config",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("show_inactive", "true");
        query.push("search_field", "tax_rate");
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  // Create new tax rate
  createTaxRate: async (
    data: CreateTaxRateRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/tax_rate");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  // Update tax rate
  updateTaxRate: async (
    data: UpdateTaxRateRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/tax_rate");
    const response = await onboardingApiClient.patch(url, data);
    return response.data;
  },

  // Toggle tax rate status
  toggleTaxRateStatus: async (
    data: ToggleTaxRateRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/tax_rate");
    const response = await onboardingApiClient.patch(url, data);
    return response.data;
  },
};

export const certificationsApi = {
  // Get all certifications with optional search
  getCertifications: async (
    utilityId: string,
  ): Promise<CertificationAPI> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/certificates",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("show_inactive", "true");
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },

  certificationsSearch: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: CertificationAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "search/onboarding_config",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("show_inactive", "true");
        query.push("search_field", "certificates");
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result;
  },

  // Create new certification
  createCertification: async (
    data: CreateCertificationRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/certificates");
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  // Update certification
  updateCertification: async (
    data: UpdateCertificationRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/certificates");
    const response = await onboardingApiClient.patch(url, data);
    return response.data;
  },

  // Toggle certification status
  toggleCertificationStatus: async (
    data: ToggleCertificationRequest
  ): Promise<{ result: string }> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/certificates");
    const response = await onboardingApiClient.patch(url, data);
    return response.data;
  },
};

export const skillsApi = {
  // Get all skills
  getSkills: async (
    utilityId: string,
    searchQuery?: string // Add optional search parameter
  ): Promise<{ result: SkillAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "skills",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new skill
  createSkill: async (
    data: CreateSkillRequest
  ): Promise<{ result: SkillAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "skills");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update skill
  updateSkill: async (
    skillId: number,
    data: UpdateSkillRequest
  ): Promise<{ result: SkillAPI }> => {
    const url = ApiEndpoints.createUrl("wx", `skills/${skillId}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle skill status
  toggleSkillStatus: async (
    skillId: number,
    data: ToggleSkillRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `skill/action/${skillId}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};

export const rolesApi = {
  // Get all roles
  getRoles: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: RoleAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "role",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new role
  createRole: async (data: CreateRoleRequest): Promise<{ result: RoleAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "role");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update role
  updateRole: async (
    roleId: number,
    data: UpdateRoleRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `role/${roleId}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle role status
  toggleRoleStatus: async (
    roleId: number,
    data: ToggleRoleRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `role-action/${roleId}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};

export const ratesApi = {
  // Get all rates
  getRates: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: RateAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "field-force-rate",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new rate
  createRate: async (data: CreateRateRequest): Promise<{ result: RateAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "field-force-rate");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update rate
  updateRate: async (
    id: number,
    data: UpdateRateRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `field-force-rate/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle isActive status
  toggleRateStatus: async (
    id: number,
    data: ToggleRateRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `field-force-rate-action/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};

export const readingsApi = {
  // Get all readings
  getReadings: async (
    utilityId: number,
    searchQuery?: string
  ): Promise<ReadingsApiResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/reading",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new reading
  createReading: async (
    data: CreateReadingRequest
  ): Promise<CreateUpdateApiResponse> => {
    const url = ApiEndpoints.createUrl("wx", "sop/reading");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update reading
  updateReading: async (
    data: UpdateReadingRequest
  ): Promise<CreateUpdateApiResponse> => {
    const url = ApiEndpoints.createUrl("wx", "sop/reading");
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
  toggleReadingStatus: async (
    data: ToggleReadingRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `sop/reading/status`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
};

export const materialsApi = {
  // Get all materials
  getMaterials: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: MaterialAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/material",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new material
  createMaterial: async (
    data: CreateMaterialRequest
  ): Promise<{ message: string; result: MaterialAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "sop/material");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update material
  updateMaterial: async (
    data: UpdateMaterialRequest
  ): Promise<{ message: string; result: MaterialAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "sop/material");
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
  toggleMaterialStatus: async (
    data: ToggleMaterialRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `sop/material/status`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
};

export const tasksApi = {
  // Get all tasks
  getTasks: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<{ result: TaskAPI[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/task",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new task
  createTask: async (
    data: CreateTaskRequest
  ): Promise<{ message: string; result: TaskAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "sop/task");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update task
  updateTask: async (
    data: UpdateTaskRequest
  ): Promise<{ message: string; result: TaskAPI }> => {
    const url = ApiEndpoints.createUrl("wx", "sop/task");
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
  toggleTaskStatus: async (
    data: ToggleTaskRequest
  ): Promise<{ message: string }> => {
    const url = ApiEndpoints.createUrl("wx", `sop/task/status`);
    const response = await wxApiClient.patch(url, data);
    return response.data;
  },
  // Get skills (placeholder - adjust endpoint as needed)
  getSkills: async (utilityId: string): Promise<{ result: Skill[] }> => {
    // TODO: Replace with actual skills endpoint when available
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "skills", // Adjust this endpoint as needed
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
      }
    );
    try {
      const response = await wxApiClient.get(url);
      return response.data;
    } catch (error) {}
  },
};

export const ppeEquipmentApi = {
  // Get all PPE equipment
  getPPEEquipment: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<PPEEquipmentResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/safety-item",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("type", "1"); // 1 for PPE type

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new PPE equipment
  createPPEEquipment: async (
    data: CreatePPEEquipmentRequest
  ): Promise<PPEEquipmentCreateResponse> => {
    const url = ApiEndpoints.createUrl("wx", "sop/safety-item");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update PPE equipment
  updatePPEEquipment: async (
    id: number,
    data: UpdatePPEEquipmentRequest
  ): Promise<PPEEquipmentUpdateResponse> => {
    const url = ApiEndpoints.createUrl("wx", `sop/safety-item/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle PPE equipment isActive status
  togglePPEEquipmentStatus: async (
    id: number,
    data: TogglePPEEquipmentRequest
  ): Promise<PPEEquipmentUpdateResponse> => {
    const url = ApiEndpoints.createUrl("wx", `sop/safety-item/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};

export const safetyPackageApi = {
  // Get all safety packages
  getSafetyPackages: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<SafetyPackageResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/safety-package",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());

        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new safety package
  createSafetyPackage: async (
    data: CreateSafetyPackageRequest
  ): Promise<SafetyPackageCreateResponse> => {
    const url = ApiEndpoints.createUrl("wx", "sop/safety-package");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update safety package
  updateSafetyPackage: async (
    id: number,
    data: UpdateSafetyPackageRequest
  ): Promise<SafetyPackageUpdateResponse> => {
    const url = ApiEndpoints.createUrl("wx", `sop/safety-package/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle safety package isActive status
  toggleSafetyPackageStatus: async (
    id: number,
    data: ToggleSafetyPackageRequest
  ): Promise<SafetyPackageUpdateResponse> => {
    const url = ApiEndpoints.createUrl("wx", `sop/safety-package/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};

export const safetyProtocolApi = {
  // Get all safety protocols
  getSafetyProtocols: async (
    utilityId: string,
    searchQuery?: string
  ): Promise<SafetyProtocolResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      "sop/safety-item",
      (query) => {
        query.push("remote_utility_id", utilityId.toString());
        query.push("type", "2"); 
        // Add search parameter if provided
        if (searchQuery && searchQuery.trim()) {
          query.push("search_data", searchQuery.trim());
        }
      }
    );
    const response = await wxApiClient.get(url);
    return response.data;
  },

  // Create new safety protocol
  createSafetyProtocol: async (
    data: CreateSafetyProtocolRequest
  ): Promise<SafetyProtocolCreateResponse> => {
    const url = ApiEndpoints.createUrl("wx", "sop/safety-item");
    const response = await wxApiClient.post(url, data);
    return response.data;
  },

  // Update safety protocol
  updateSafetyProtocol: async (
    id: number,
    data: UpdateSafetyProtocolRequest
  ): Promise<SafetyProtocolUpdateResponse> => {
    const url = ApiEndpoints.createUrl("wx", `sop/safety-item/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },

  // Toggle safety protocol isActive status
  toggleSafetyProtocolStatus: async (
    id: number,
    data: ToggleSafetyProtocolRequest
  ): Promise<SafetyProtocolUpdateResponse> => {
    const url = ApiEndpoints.createUrl("wx", `sop/safety-item/${id}`);
    const response = await wxApiClient.put(url, data);
    return response.data;
  },
};
